import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import BaziCard from '../../components/BaziCard';
import Pagination from '../../components/Pagination';
import { EmptyState, SkeletonGrid } from '../../components/ListStates';
import { baziAPI } from '../../api/api';
import { getPinyinInitial } from '../../utils/pinyin';
import './Community.css';
import FilterBar from '../../components/CommunityFilters/FilterBar';
import AdvancedFiltersModal from '../../components/CommunityFilters/AdvancedFiltersModal';
import { User, X } from 'lucide-react';

const PAGE_SIZE = 24;
const EMPTY_ADV = {
  name: '',
  gender: '',
  calendarType: 'gregorian',
  birthYearFrom: '',
  birthYearTo: '',
  birthMonth: '',
  birthDay: '',
  yearGan: '', yearZhi: '',
  monthGan: '', monthZhi: '',
  dayGan: '', dayZhi: '',
  hourGan: '', hourZhi: ''
};

const Community = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState('');
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [share, setShare] = useState('');
  const [publisherId, setPublisherId] = useState('');
  const [publisherLabel, setPublisherLabel] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advFilters, setAdvFilters] = useState({ ...EMPTY_ADV });
  const [page, setPage] = useState(1);
  const [applyNonce, setApplyNonce] = useState(0);
  const [pendingScroll, setPendingScroll] = useState('');
  const navigate = useNavigate();

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    fetchCommunityRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 由「应用筛选 / 清除 / 移除筛选 chip」触发的服务端重新拉取
  useEffect(() => {
    if (applyNonce > 0) fetchCommunityRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyNonce]);

  const buildParams = () => {
    const params = {};
    if (selectedLabels.length > 0) params.labels = selectedLabels.join(',');
    if (share) params.share = share;
    if (publisherId) params.publisherId = publisherId;
    if (advFilters.name) params.name = advFilters.name.trim();
    if (advFilters.gender) params.gender = advFilters.gender;
    if (advFilters.calendarType) params.calendarType = advFilters.calendarType;
    if (advFilters.birthYearFrom) params.birthYearFrom = advFilters.birthYearFrom;
    if (advFilters.birthYearTo) params.birthYearTo = advFilters.birthYearTo;
    if (advFilters.birthMonth) params.birthMonth = advFilters.birthMonth;
    if (advFilters.birthDay) params.birthDay = advFilters.birthDay;
    ['yearGan', 'yearZhi', 'monthGan', 'monthZhi', 'dayGan', 'dayZhi', 'hourGan', 'hourZhi'].forEach((k) => {
      const v = advFilters[k];
      if (v && String(v).trim()) params[k] = String(v).trim();
    });
    return params;
  };

  const fetchCommunityRecords = async () => {
    try {
      setLoading(true);
      const params = buildParams();
      const response = await baziAPI.getCommunity(params);
      const data = response.data.data;
      const recordsWithPinyin = data.map((record) => ({
        ...record,
        pinyinInitial: getPinyinInitial(record.name)
      }));
      recordsWithPinyin.sort((a, b) => a.pinyinInitial.localeCompare(b.pinyinInitial));
      setRecords(recordsWithPinyin);
      setPage(1);
      setError('');
    } catch (err) {
      setError('加载失败，请重试');
      console.error('获取社区记录失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const requestApply = () => setApplyNonce((n) => n + 1);

  const applyFilters = () => requestApply();

  const clearFilters = () => {
    setSelectedLabels([]);
    setShare('');
    setPublisherId('');
    setPublisherLabel('');
    setAdvFilters({ ...EMPTY_ADV });
    setSearchTerm('');
    requestApply();
  };

  // 本地按姓名过滤（即时）
  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records;
    const q = searchTerm.trim().toLowerCase();
    return records.filter((r) => (r.name || '').toLowerCase().includes(q));
  }, [records, searchTerm]);

  useEffect(() => { setPage(1); }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const pageRecords = filteredRecords.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  // 每个首字母在全量过滤结果中首次出现的页码（供字母索引跳页）
  const letterToPage = useMemo(() => {
    const map = {};
    filteredRecords.forEach((r, i) => {
      const initial = r.pinyinInitial;
      if (map[initial] == null) map[initial] = Math.floor(i / PAGE_SIZE) + 1;
    });
    return map;
  }, [filteredRecords]);

  // 当前页内按首字母分组（页内顺序）
  const pageGroups = useMemo(() => {
    const groups = [];
    let cur = null;
    pageRecords.forEach((r) => {
      if (!cur || cur.initial !== r.pinyinInitial) {
        cur = { initial: r.pinyinInitial, items: [] };
        groups.push(cur);
      }
      cur.items.push(r);
    });
    return groups;
  }, [pageRecords]);

  // 跳页后滚动到目标分组
  useEffect(() => {
    if (!pendingScroll) return;
    const el = document.getElementById(`section-${pendingScroll}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingScroll('');
  }, [pendingScroll, curPage]);

  const scrollToLetter = (letter) => {
    const targetPage = letterToPage[letter];
    if (!targetPage) return;
    setActiveIndex(letter);
    if (targetPage !== curPage) {
      setPage(targetPage);
      setPendingScroll(letter);
    } else {
      const el = document.getElementById(`section-${letter}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const changePage = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePublisher = (id, label) => {
    setPublisherId(id);
    setPublisherLabel(id ? (label || '') : '');
  };

  // 已应用筛选 chips
  const activeChips = useMemo(() => {
    const chips = [];
    if (searchTerm.trim()) {
      chips.push({ key: 'search', label: `搜索：${searchTerm.trim()}`, remove: () => setSearchTerm('') });
    }
    selectedLabels.forEach((name) => {
      chips.push({
        key: `label-${name}`,
        label: `标签：${name}`,
        remove: () => { setSelectedLabels((prev) => prev.filter((n) => n !== name)); requestApply(); }
      });
    });
    if (share) {
      chips.push({
        key: 'share',
        label: `分享：${share === 'public' ? '公开' : '限制（分享给我）'}`,
        remove: () => { setShare(''); requestApply(); }
      });
    }
    if (publisherId) {
      chips.push({
        key: 'publisher',
        label: `发布者：${publisherLabel || publisherId}`,
        remove: () => { handlePublisher('', ''); requestApply(); }
      });
    }
    if (advFilters.gender) {
      chips.push({
        key: 'gender',
        label: `性别：${advFilters.gender}`,
        remove: () => { setAdvFilters((prev) => ({ ...prev, gender: '' })); requestApply(); }
      });
    }
    const hasDate = advFilters.birthYearFrom || advFilters.birthYearTo || advFilters.birthMonth || advFilters.birthDay;
    if (hasDate) {
      const cal = advFilters.calendarType === 'lunar' ? '农历' : '阳历';
      const range = [advFilters.birthYearFrom, advFilters.birthYearTo].filter(Boolean).join('–');
      chips.push({
        key: 'date',
        label: `日期：${range || '范围'}（${cal}）`,
        remove: () => {
          setAdvFilters((prev) => ({ ...prev, birthYearFrom: '', birthYearTo: '', birthMonth: '', birthDay: '' }));
          requestApply();
        }
      });
    }
    ['year', 'month', 'day', 'hour'].forEach((p) => {
      const gan = advFilters[`${p}Gan`];
      const zhi = advFilters[`${p}Zhi`];
      if (gan || zhi) {
        const pLabel = { year: '年', month: '月', day: '日', hour: '时' }[p];
        chips.push({
          key: `pillar-${p}`,
          label: `${pLabel}柱：${gan || ''}${zhi || ''}`,
          remove: () => { setAdvFilters((prev) => ({ ...prev, [`${p}Gan`]: '', [`${p}Zhi`]: '' })); requestApply(); }
        });
      }
    });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedLabels, share, publisherId, publisherLabel, advFilters]);

  return (
    <div className="community">
      <Navbar />
      <div className="container">
        <div className="community-header">
          <h1>社区八字</h1>
          {!loading && !error && <span className="community-count">共 {filteredRecords.length} 例</span>}
        </div>

        <div className="community-filters-card">
          <FilterBar
            search={searchTerm}
            onChangeSearch={setSearchTerm}
            labels={selectedLabels}
            onChangeLabels={setSelectedLabels}
            share={share}
            onChangeShare={setShare}
            publisherId={publisherId}
            onChangePublisherId={handlePublisher}
            onOpenAdvanced={() => setShowAdvanced(true)}
            onApply={applyFilters}
            onClear={clearFilters}
          />
          {activeChips.length > 0 && (
            <div className="community-active-chips">
              {activeChips.map((c) => (
                <span className="active-chip" key={c.key}>
                  {c.label}
                  <button type="button" onClick={c.remove} aria-label="移除筛选"><X size={13} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="community-content">
          <div className="community-list">
            {loading ? (
              <SkeletonGrid count={9} className="community-grid" />
            ) : error ? (
              <div className="list-empty">
                <p className="list-empty-text">{error}</p>
                <button className="btn btn-secondary" onClick={fetchCommunityRecords}>重试</button>
              </div>
            ) : filteredRecords.length === 0 ? (
              <EmptyState message={searchTerm || activeChips.length ? '没有找到匹配的命例' : '社区还没有任何八字记录'} />
            ) : (
              <>
                {pageGroups.map((group) => (
                  <div key={group.initial} id={`section-${group.initial}`} className="letter-section">
                    <div className="letter-header">{group.initial}</div>
                    <div className="community-grid">
                      {group.items.map((record) => (
                        <BaziCard
                          key={record._id}
                          record={record}
                          publisher={record.userId?.username}
                          onOpen={(id) => navigate(`/bazi/${id}`)}
                          actions={record.userId?._id ? [
                            { key: 'pub', label: '查看发布者', icon: <User size={15} />, onClick: () => navigate(`/users/${record.userId._id}`) }
                          ] : []}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                <Pagination page={curPage} totalPages={totalPages} onChange={changePage} />
              </>
            )}
          </div>

          {/* 字母索引侧边栏 */}
          <div className="alphabet-sidebar">
            {alphabet.map((letter) => {
              const hasRecords = letterToPage[letter] != null;
              return (
                <button
                  key={letter}
                  className={`alphabet-btn ${activeIndex === letter ? 'active' : ''} ${!hasRecords ? 'disabled' : ''}`}
                  onClick={() => hasRecords && scrollToLetter(letter)}
                  disabled={!hasRecords}
                >
                  {letter}
                </button>
              );
            })}
            {letterToPage['#'] != null && (
              <button
                className={`alphabet-btn ${activeIndex === '#' ? 'active' : ''}`}
                onClick={() => scrollToLetter('#')}
              >
                #
              </button>
            )}
          </div>
        </div>
      </div>
      {showAdvanced && (
        <AdvancedFiltersModal
          show={showAdvanced}
          value={advFilters}
          onChange={setAdvFilters}
          onApply={() => { setShowAdvanced(false); applyFilters(); }}
          onClose={() => setShowAdvanced(false)}
        />
      )}
    </div>
  );
};

export default Community;
